import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col } from "reactstrap";
import Coverage from "./Coverage";
import AnalyticsCard from "./AnalyticsCard";
import PartiallyIncompletedAttributes from "./PartiallyIncompletedAttributes";
import {
  target_group,
  product_category,
  style,
  price,
  brand_rating_options,
  getCamelCaseWord
} from "../../static/brands_wholesalers_data";
import SizeImg from '../../static/take-look-black.png';
import { getCoverageData } from "../../ReduxToolkit/Reducers/analytics";
import { useNavigate } from "react-router-dom";
import { setSelectedFilters } from "../../ReduxToolkit/Reducers/brands";

// Attributes that are the ONLY missing one on a partially incompleted profile.
// Card titles double as the only_missing_attribute filter value lookup below.
const ONLY_MISSING_ICONS = {
  target_group: () => target_group[0]?.imageBlack,
  product_category: () => product_category[0]?.imageBlack,
  style: () => style[0]?.imageBlack,
  price: () => price[0]?.imageBlack,
}

const onlyMissingCardTitle = (label) => `${label} Is Only Missing Attribute In`

// Card title -> the label the list filter expects, e.g. "Target Group".
export const onlyMissingLabelFromTitle = (coverage, title) =>
  (coverage?.only_missing_attributes || []).find(
    (item) => onlyMissingCardTitle(item.label) === title,
  )?.label

const onlyMissingCards = (coverage) =>
  (coverage?.only_missing_attributes || []).map((item) => ({
    title: onlyMissingCardTitle(item.label),
    count: item.count || 0,
    icon: ONLY_MISSING_ICONS[item.attribute]?.(),
    isImageIcon: true,
  }))

const BrandsAnalytics = () => {
  const [location, setLocation] = useState("")
  const [createdDateRange, setCreatedDateRange] = useState({ startDate: null, endDate: null })
  const [updatedDateRange, setUpdatedDateRange] = useState({ startDate: null, endDate: null })
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const { brandsCoverage, isLoading } = useSelector((state) => state.analytics);

  useEffect(() => {
    dispatch(getCoverageData('Brands'));
  }, [dispatch]);

  const handleLocationSelect = (selectedLocation) => {
    setLocation(selectedLocation)
    dispatch(getCoverageData(
      'Brands',
      selectedLocation || null,
      null,
      createdDateRange?.startDate ? createdDateRange : null,
      updatedDateRange?.startDate ? updatedDateRange : null
    ));
  };

  const handleDateRangeChange = (newCreatedDateRange, newUpdatedDateRange) => {
    dispatch(getCoverageData(
      'Brands',
      location || null,
      null,
      newCreatedDateRange?.startDate ? newCreatedDateRange : null,
      newUpdatedDateRange?.startDate ? newUpdatedDateRange : null
    ));
  };

  // Must mirror the analytics query's own scope exactly (fashion / valid website /
  // brand or monobrand entity / the dashboard's date range), or the table count will
  // not match the card that linked to it.
  const getBaseFilters = () => ({
    summary_type: ['Fashion'],
    entity: ['Brand', 'Monobrand'],
    website_status: ['Valid'],
    ...(createdDateRange?.startDate && { createdStartDate: createdDateRange.startDate }),
    ...(createdDateRange?.endDate && { createdEndDate: createdDateRange.endDate }),
    ...(updatedDateRange?.startDate && { updatedStartDate: updatedDateRange.startDate }),
    ...(updatedDateRange?.endDate && { updatedEndDate: updatedDateRange.endDate }),
  })

  // Jump to the brands list scoped to the partially incompleted profiles that are
  // missing this specific attribute.
  const handleAttributeView = (attribute) => {
    const filterKeyByAttribute = {
      target_group: 'target group',
      product_category: 'categories',
      style: 'style',
      price: 'price',
    }
    const filterKey = filterKeyByAttribute[attribute]
    if (!filterKey) return

    const filters = { ...getBaseFilters(), profile_status: ['Partially Incomplete'], [filterKey]: ['Null Records'] }
    if (location) filters.location = [getCamelCaseWord(location)]

    dispatch(setSelectedFilters({ filters }))
    navigate('/brands')
  }

  const handleView = (cardName) => {
    let filters = getBaseFilters()

    if (cardName === "Completed Brands") {
      filters.profile_status = ['Completed']
    } else if (cardName === "Partially Incompleted Brands") {
      filters.profile_status = ['Partially Incomplete']
    } else if (cardName === "Total Empty Profiles") {
      filters.profile_status = ['Empty']
    } else if (cardName === "Brands With Matches") {
      filters.match_status = ["With Matches"]
    } else if (cardName === "Brands With Next to/Competitor") {
      filters.competitor_status = ['With Next to Competitor']
    } else if (cardName === "Completed With Working With" || cardName === "Completed Without Working With") {
      filters.working_with_profile_status = [cardName]
    } else if (cardName === "Partially Incompleted With Working With") {
      filters.working_with_profile_status = ['Partially Incompleted With Working With']
    } else if (cardName === "Partially Incompleted Without Working With") {
      filters.working_with_profile_status = ['Partially Incompleted Without Working With']
    } else {
      // "<Attribute> Is Only Missing Attribute In" cards
      const onlyMissingLabel = onlyMissingLabelFromTitle(brandsCoverage, cardName)
      if (onlyMissingLabel) filters.only_missing_attribute = [onlyMissingLabel]
    }
    if (location) filters.location = [getCamelCaseWord(location)]

    if (Object.keys(filters).length > 0) dispatch(setSelectedFilters({ filters }))
    console.log(filters)
    navigate('/brands')
  }

  const coverageData = brandsCoverage ? {
    map: brandsCoverage?.map || {},
    cards: [
      { title: "Total Brands (completed + partially incompleted)", count: brandsCoverage?.total_completed + brandsCoverage?.total_incompleted || 0 },
      { title: "Completed Brands", count: brandsCoverage?.total_completed || 0 },
      { title: "Partially Incompleted Brands", count: brandsCoverage?.total_incompleted || 0 },
      { title: "Brands With Matches", count: brandsCoverage?.with_matches || 0 },
      { title: "Brands With Next to/Competitor", count: brandsCoverage?.with_next_to_competitor || 0 },
      { title: "Total Empty Profiles", count: brandsCoverage?.total_empty || 0 }
    ],
  } : null;

  // Calculate "Present" counts from API's "not_present" counts
  // Total brands = completed + incompleted

  const additionalCards = [
    {
      title: "Completed With Working With",
      count: brandsCoverage?.completed_with_working_with || 0,
      isImageIcon: false
    },
    {
      title: "Completed Without Working With",
      count: brandsCoverage?.completed_without_working_with || 0,
      isImageIcon: false
    },
    {
      title: "Partially Incompleted With Working With",
      count: brandsCoverage?.incompleted_with_working_with || 0,
      isImageIcon: false
    },
    {
      title: "Partially Incompleted Without Working With",
      count: brandsCoverage?.incompleted_without_working_with || 0,
      isImageIcon: false
    },
    ...onlyMissingCards(brandsCoverage),
  ];

  return (
    <div>
      {coverageData && (
        <Coverage
          data={coverageData}
          loading={isLoading}
          onLocationSelect={handleLocationSelect}
          onCardViewClick={handleView}
          location={location}
          setLocation={setLocation}
          createdDateRange={createdDateRange}
          setCreatedDateRange={setCreatedDateRange}
          updatedDateRange={updatedDateRange}
          setUpdatedDateRange={setUpdatedDateRange}
          onDateRangeChange={handleDateRangeChange}
        />
      )}

      <PartiallyIncompletedAttributes
        attributes={brandsCoverage?.incompleted_attributes}
        total={brandsCoverage?.partially_incompleted_total}
        isLoading={isLoading}
        onViewClick={handleAttributeView}
      />

      <div style={{ border: '0.5px solid #d1d1d1', margin: '30px 0px' }} />

      <Row className="g-3 mt-3">
        {additionalCards.map((card, index) => (
          <Col sm={6} md={4} key={index}>
            <AnalyticsCard
              title={card.title}
              count={card.count}
              icon={card.icon}
              isLoading={isLoading}
              isImageIcon={card.isImageIcon}
              showViewButton={card.showViewButton !== false}
              onViewClick={handleView}
            />
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default BrandsAnalytics;